import { Box, Typography } from '@mui/material';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import type { ChartSpec } from '../services/analysis-service';

/** Palette matches the app's other charts rather than recharts' defaults. */
export const SERIES_COLOURS = ['#2f6f62', '#c2874e', '#5b7fa6', '#8a6ea8', '#a85f5f', '#6f8f4a'];

/** Draws one chart spec.
 *
 * ANIMATION IS OFF DELIBERATELY. Recharts animates a bar up from zero on
 * mount; inside a Dialog the container is measured before the open
 * transition finishes, and the animation resolves against a stale size and
 * settles at zero height. The axes still scale to the data, so the chart
 * renders complete and empty -- which reads as "no data" rather than as a
 * bug, on a page whose whole job is to report numbers.
 * An unknown type renders its data as a table rather
 * than nothing, so a model inventing a chart kind still communicates. */
/** Formats a chart value for humans.
 *
 * Raw axis and label numbers like "6987998" are unreadable and, on a page
 * about money, ambiguous: the reader cannot tell dollars from percent from a
 * share count. Percent-titled charts render as %, everything else as
 * abbreviated currency, with the full figure in the tooltip.
 */
function isPercentChart(title?: string) {
  return /%|percent|share|allocation/i.test(title ?? '');
}

function compact(n: number, percent: boolean) {
  if (percent) return `${n.toFixed(2)}%`;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function full(n: number, percent: boolean) {
  if (percent) return `${n.toFixed(2)}%`;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(n);
}

export function ChartBlock({ spec }: { spec: ChartSpec }) {
  const series = spec.series ?? [];
  if (!series.length) return null;
  const pct = isPercentChart(spec.title);
  const tickFmt = (v: number) => compact(v, pct);
  const tipFmt = (v: number | string) => full(Number(v), pct);

  const data = (series[0].points ?? []).map((p, i) => {
    const row: Record<string, string | number> = { x: p.x };
    series.forEach((s) => { row[s.name] = s.points?.[i]?.y ?? 0; });
    return row;
  });

  return (
    <Box sx={{ my: 2 }}>
      {spec.title && <Typography variant="subtitle2" gutterBottom>{spec.title}</Typography>}
      <Box sx={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          {spec.type === 'line' ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="x" fontSize={12} />
              <YAxis fontSize={12} tickFormatter={tickFmt} width={70} />
              <RTooltip formatter={tipFmt} /><Legend />
              {series.map((s, i) => (
                <Line key={s.name} dataKey={s.name} stroke={SERIES_COLOURS[i % SERIES_COLOURS.length]}
                  dot={false} isAnimationActive={false} />
              ))}
            </LineChart>
          ) : spec.type === 'pie' ? (
            <PieChart>
              <RTooltip formatter={tipFmt} />
              <Pie
                data={data}
                dataKey={series[0].name}
                nameKey="x"
                outerRadius={90}
                isAnimationActive={false}
                label={(e: { name?: string; value?: number; percent?: number }) =>
                  `${e.name}: ${full(Number(e.value ?? 0), pct)} (${((e.percent ?? 0) * 100).toFixed(1)}%)`}
              >
                {data.map((_, i) => <Cell key={i} fill={SERIES_COLOURS[i % SERIES_COLOURS.length]} />)}
              </Pie>
            </PieChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="x" fontSize={12} />
              <YAxis fontSize={12} tickFormatter={tickFmt} width={70} />
              <RTooltip formatter={tipFmt} /><Legend />
              {series.map((s, i) => (
                <Bar key={s.name} dataKey={s.name} fill={SERIES_COLOURS[i % SERIES_COLOURS.length]}
                  isAnimationActive={false} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
